/**
 * FilterPanel Component - Reusable filter panel with export button
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Badge } from './badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Filter, X, Search, ChevronDown, ChevronUp, RotateCcw, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { cn } from '../../lib/utils';
import './FilterPanel.css';

/**
 * @param {Object} props
 * @param {Array} props.filters - Array of filter definitions
 * @param {Object} props.values - Current filter values
 * @param {Function} props.onChange - Callback when filter values change
 * @param {Function} props.onSearch - Callback to execute search
 * @param {Function} props.onClear - Callback to clear all filters
 * @param {Function} props.onExportCSV - Callback to export CSV
 * @param {Function} props.onExportJSON - Callback to export JSON
 * @param {boolean} props.showExport - Show export button
 */
export function FilterPanel({
  filters = [],
  values = {},
  onChange,
  onSearch,
  onClear,
  onExportCSV,
  onExportJSON,
  showExport = true,
  className,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const activeFilters = filters.filter(f => {
    const value = values[f.key];
    if (value === undefined || value === '' || value === 'all') return false;
    return true;
  });

  const handleChange = (key, value) => {
    onChange({ ...values, [key]: value });
  };

  const handleRemoveFilter = (key) => {
    const newValues = { ...values };
    delete newValues[key];
    onChange(newValues);
    setTimeout(() => onSearch?.(), 0);
  };

  const handleClear = () => {
    onClear?.();
    setIsOpen(false);
  };

  const getFilterLabel = (filter) => {
    const value = values[filter.key];
    if (filter.type === 'select' && filter.options) {
      const option = filter.options.find(o => o.value === value);
      return option?.label || value;
    }
    return value;
  };

  return (
    <div className={cn('filter-panel', className)}>
      {/* Toolbar Row - Filters + Export on same line */}
      <div className="filter-panel-toolbar">
        <div className="filter-panel-left">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="filter-toggle-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            {t('common.filters') || 'Filtros'}
            {activeFilters.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilters.length}
              </Badge>
            )}
            {isOpen ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </Button>

          {/* Active Filter Tags */}
          {activeFilters.length > 0 && (
            <>
              <div className="filter-tags">
                {activeFilters.map(filter => (
                  <Badge
                    key={filter.key}
                    variant="secondary"
                    className="filter-tag"
                  >
                    <span className="filter-tag-label">{filter.label}:</span>
                    <span className="filter-tag-value">{getFilterLabel(filter)}</span>
                    <button
                      onClick={() => handleRemoveFilter(filter.key)}
                      className="filter-tag-remove"
                      data-testid={`remove-filter-${filter.key}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                data-testid="clear-filters-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                {t('common.clearFilters') || 'Limpar'}
              </Button>
            </>
          )}
        </div>

        {/* Export Dropdown - Right side */}
        {showExport && (onExportCSV || onExportJSON) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="export-dropdown-btn">
                <Download className="h-4 w-4" />
                Exportar
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onExportCSV && (
                <DropdownMenuItem onClick={onExportCSV}>
                  <FileText className="h-4 w-4 mr-2" />
                  Exportar CSV
                </DropdownMenuItem>
              )}
              {onExportJSON && (
                <DropdownMenuItem onClick={onExportJSON}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Exportar JSON
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Filter Panel Content - Expandable */}
      {isOpen && (
        <div className="filter-panel-content">
          <div className="filter-panel-fields">
            {filters.map(filter => (
              <div key={filter.key} className="filter-field">
                <Label htmlFor={filter.key} className="text-sm">
                  {filter.label}
                </Label>
                {filter.type === 'text' && (
                  <Input
                    id={filter.key}
                    value={values[filter.key] || ''}
                    onChange={(e) => handleChange(filter.key, e.target.value)}
                    placeholder={filter.placeholder}
                    className="h-9"
                  />
                )}
                {filter.type === 'date' && (
                  <Input
                    id={filter.key}
                    type="date"
                    value={values[filter.key] || ''}
                    onChange={(e) => handleChange(filter.key, e.target.value)}
                    className="h-9"
                  />
                )}
                {filter.type === 'select' && (
                  <Select
                    value={values[filter.key] || ''}
                    onValueChange={(value) => handleChange(filter.key, value)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={filter.placeholder || 'Selecione'} />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.options?.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>
          <div className="filter-panel-actions">
            <Button onClick={onSearch} data-testid="apply-filters-btn">
              <Search className="h-4 w-4 mr-2" />
              {t('common.search') || 'Buscar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterPanel;
